import type { QueryStringClient } from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import { isMyPortalNotification, MY_PORTAL_NOTIFICATION_QUERY_PARAM } from '@/scopes/common/domain/myPortalNotification'
import browserQueryStringClient from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import bookingsOverviewApiClient from '../infrastructure/bookingsOverviewApiClient'
import { bookingsOverviewStore } from '../infrastructure/store'
import { AddBookingPayload } from '../infrastructure/types'
import { UpcomingBooking } from './types'

const BANNER_DISPLAY_TIME_MS = 5000

export interface BookingsOverviewUseCases {
  apiClient: typeof bookingsOverviewApiClient
  store: typeof bookingsOverviewStore
  queryStringClient: QueryStringClient
}

export function getFirstUpcomingBookingId(upcomingBookings: Record<string, UpcomingBooking>): string | null {
  const bookings = Object.values(upcomingBookings)
    .map(b => ({ ...b, departureTs: Date.parse(b.rawDepartureDate) }))
    .filter(b => !Number.isNaN(b.departureTs))

  if (bookings.length === 0)
    return null

  bookings.sort((a, b) => a.departureTs - b.departureTs)

  return bookings[0].bookingNumber
}

export function createBookingsOverviewUseCases({
  apiClient,
  store,
  queryStringClient,
}: BookingsOverviewUseCases) {
  let bannerTimer: ReturnType<typeof setTimeout> | null = null
  let myPortalNotificationTimer: ReturnType<typeof setTimeout> | null = null

  function takeMyPortalNotificationFromUrl() {
    const notification = queryStringClient.getValue(MY_PORTAL_NOTIFICATION_QUERY_PARAM)
    if (!notification)
      return null

    queryStringClient.remove(MY_PORTAL_NOTIFICATION_QUERY_PARAM)
    return isMyPortalNotification(notification) ? notification : null
  }

  async function pageLoaded(contextItemId: string) {
    const myPortalNotification = takeMyPortalNotificationFromUrl()
    const state = store.getState()
    state.setIn('bookingOverviewPageLoadStatus', 'loading')
    try {
      const [bookingsResult, discountResult, vouchersResult] = await Promise.allSettled([
        apiClient.getBookingsOverviewData(contextItemId),
        apiClient.getDiscountData(),
        apiClient.getVouchers(),
      ])

      if (bookingsResult.status === 'rejected' || !bookingsResult.value) {
        state.setIn('bookingOverviewPageLoadStatus', 'error')
        return
      }
      const data = bookingsResult.value
      const discount = discountResult.status === 'fulfilled' ? discountResult.value : null
      const vouchers = vouchersResult.status === 'fulfilled' ? vouchersResult.value : {}

      state.initialize({
        cancelledBookings: data.cancelledBookings ?? {},
        pastBookings: data.pastBookings ?? {},
        upcomingBookings: data.upcomingBookings ?? {},
        vouchers,
        discount,
        selectedUpcomingBookingId: getFirstUpcomingBookingId(data.upcomingBookings ?? {}),
      })
      state.setIn('bookingOverviewPageLoadStatus', 'success')

      if (myPortalNotification) {
        if (myPortalNotificationTimer)
          clearTimeout(myPortalNotificationTimer)

        state.setIn('myPortalNotification', myPortalNotification)
        myPortalNotificationTimer = setTimeout(() => {
          state.setIn('myPortalNotification', null)
          myPortalNotificationTimer = null
        }, BANNER_DISPLAY_TIME_MS)
      }
    }
    catch {
      state.setIn('bookingOverviewPageLoadStatus', 'error')
    }
  }

  async function addBooking({
    lastName,
    bookingNumber,
    departureDate,
  }: AddBookingPayload, contextItemId: string) {
    let data
    const state = store.getState()

    if (state.addBookingLoadStatus === 'loading')
      return

    const payload = {
      lastName: lastName.trim(),
      bookingNumber: bookingNumber.trim(),
      departureDate,
    }

    state.setIn('addBookingReasonNotLinked', null)
    state.setIn('addBookingLoadStatus', 'loading')
    try {
      data = await apiClient.addBooking(payload)

      if (!data) {
        state.setIn('addBookingLoadStatus', 'error')
        return
      }

      if (data.linked) {
        state.setIn('addBookingReasonNotLinked', null)
        try {
          const updatedData = await apiClient.getBookingsOverviewData(contextItemId)
          if (updatedData) {
            state.initialize({
              cancelledBookings: updatedData.cancelledBookings ?? {},
              pastBookings: updatedData.pastBookings ?? {},
              upcomingBookings: updatedData.upcomingBookings ?? {},
              discount: state.discount,
              vouchers: state.vouchers,
              selectedUpcomingBookingId: getFirstUpcomingBookingId(updatedData.upcomingBookings ?? {}),
            })
          }
        }
        catch {}
        state.setIn('addBookingLoadStatus', 'success')
        if (state.isAddBookingSheetOpen) {
          state.setIn('isAddBookingSheetOpen', false)
        }
        if (bannerTimer)
          clearTimeout(bannerTimer)
        bannerTimer = setTimeout(() => {
          state.setIn('addBookingLoadStatus', 'idle')
          bannerTimer = null
        }, BANNER_DISPLAY_TIME_MS)
      }
      else {
        state.setIn('addBookingReasonNotLinked', data.reasonNotLinked)
        state.setIn('addBookingLoadStatus', 'error')
      }
    }
    catch {
      state.setIn('addBookingLoadStatus', 'error')
    }
  }

  return {
    pageLoaded,
    addBooking,
  }
}

export default createBookingsOverviewUseCases({
  apiClient: bookingsOverviewApiClient,
  store: bookingsOverviewStore,
  queryStringClient: browserQueryStringClient,
})
